import { Plus, UserPlus } from 'lucide-react';
import TeamPlayersApplicationFilters, { TeamPlayersFilterValues } from './players-filters/players-filters';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Pages } from 'modules/navigation/domain/enums/pages';
import PlayersComponentsPlayerCard from './player-card';
import UDButton from 'ud-ui/components/button';
import { theme } from 'styles/theme/theme';
import { useModalAddPlayer } from 'modules/teams/ui/hooks/use-modal-add-player';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';

type TeamsComponentsFormPlayersListPartProps = {
  teamId: number;
  players: TeamPlayer[];
};

const TeamsComponentsFormPlayersListPart = (props: TeamsComponentsFormPlayersListPartProps) => {
  const navigate = useNavigate();
  const { teamId, players } = props;

  const playerId = Number(useLocation().state?.fromPlayer);

  const [name, setName] = useState<string>('');
  const playersList = useMemo(() => {
    if (!teamId && !players.length) {
      return [];
    }

    return players.filter((player) => {
      const query = name.toLowerCase();
      const firstName = player.firstName.toLowerCase();
      const surname = player.lastName.toLowerCase();
      const middleName = player.middleName ? player.middleName.toLowerCase() : '';

      return firstName.includes(query) || surname.includes(query) || middleName.includes(query);
    });
  }, [name, players, teamId]);

  const handleFilterChange = useCallback((values: TeamPlayersFilterValues) => {
    setName(values.name);
  }, []);

  const handleToCreateNewPlayer = useCallback(() => {
    navigate(Pages.TEAMS.builders.edit(teamId, 'players/create'), {
      state: playerId && { fromPlayer: playerId },
    });
  }, [navigate, playerId, teamId]);

  const { open: openModalAddPlayer, Modal: ModalAddPlayer } = useModalAddPlayer({ teamId });

  if (players.length === 0) {
    return (
      <div className="w-full px-6 pt-6">
        <div className="flex flex-col flex-center mt-24">
          <div className="text body-text bold mb-2">В команде пока нет игроков</div>
          <div className="text body-text">Давайте добавим?</div>
          <div className="flex flex-col mt-6 gap-2">
            <UDButton className="flex items-center" variant="dark" onClick={openModalAddPlayer}>
              <UserPlus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
              <span>Добавить игрока</span>
            </UDButton>

            <UDButton className="flex items-center w-full" variant="dark" onClick={handleToCreateNewPlayer}>
              <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
              <span>Создать игрока</span>
            </UDButton>
          </div>
        </div>
        {ModalAddPlayer}
      </div>
    );
  }

  return (
    <div className="pb-6">
      <div className="flex justify-between mb-6">
        <TeamPlayersApplicationFilters name={name} inputPlaceholder="Поиск по игроку" onChange={handleFilterChange} className="w-1/2" />
        <div className="flex flex-row gap-5">
          <UDButton className="flex items-center" variant="dark" onClick={openModalAddPlayer}>
            <UserPlus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
            <span>Добавить игрока</span>
          </UDButton>
          <UDButton className="flex items-center" variant="dark" onClick={handleToCreateNewPlayer}>
            <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
            <span>Создать игрока</span>
          </UDButton>
        </div>
      </div>

      {playersList.map((player, index) => (
        <PlayersComponentsPlayerCard key={player.id} teamId={teamId} player={player} index={index} />
      ))}

      {ModalAddPlayer}
    </div>
  );
};

export default TeamsComponentsFormPlayersListPart;
